import {Button, Form, message, Popconfirm, Select, Tag, Tooltip} from "antd";
import React, {useContext, useEffect, useState} from "react";
import server from "../../../../server";
import Grid from "../../../../common/table";
import dayjs from "dayjs";
import Locale from "../../../../core/locale/locale";
import EmptyData from "../../../common/empty-data/empty-data";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    FolderAddOutlined,
    InboxOutlined,
    QuestionCircleOutlined, RollbackOutlined
} from "@ant-design/icons";
import UserLoginContext from "../../../../context/userLoginContext";
import {Return_Asset_Status} from "../../../../constant/constant";
import {removeUnderScore} from "../../../../utility/utility";
import AssetRejectModal from "../utils/asset-reject-modal";
import AssetAllocateModal from "../utils/asset-allocate-modal";
import {IAllocateAssetsFormData, IAssetTabState} from "../../types/asset.types";
import {allocateAssetModalMapper} from "../actions/asset.action";
import {
    AssetAllocationSearch,
    AssetAllocationStatus
} from "../../../../domains/models/asset-management/asset-allocation";
import {TriangleAlert, Undo2} from "lucide-react";
import LoadingSkeleton from "../../../common/loading-skeleton/loading-skeleton";

const AssetsTab = ({employeeData}: any) => {
    const {user} = useContext(UserLoginContext);
    const [assetRejectForm] = Form.useForm();
    const [assetAllocateForm] = Form.useForm();
    const [returnStatus, setReturnStatus] = useState("");
    const pageSize = 100;
    const [loading, setLoading] = useState(true);
    const [state, setState] = useState<IAssetTabState>({
        modal: {
            assetReject: false,
            assetAllocate: false,
        },
        data: {
            allocatedAssets: [],
            requestedAssets: [],
        },
        selected: {
            RequestId: "",
            AllocateRecord: null
        }
    })

    useEffect(() => {
        getAssets();
    }, [employeeData.id])

    async function getAssets() {
        setLoading(true);
        try {
            const assetAllocatedDataPayload: AssetAllocationSearch = {
                employeeId: employeeData.id,
                status: [AssetAllocationStatus.ALLOCATED, AssetAllocationStatus.MISSING, AssetAllocationStatus.RETURNED]
            }
            const assetRequestedDataPayload: AssetAllocationSearch = {
                employeeId: employeeData.id,
                status: [AssetAllocationStatus.REQUEST_CANCELLED, AssetAllocationStatus.REQUEST_REJECTED, AssetAllocationStatus.REQUESTED, AssetAllocationStatus.REQUEST_ACCEPTED]
            }
            const allAllocatedAssets = await server.asset.allocation.search(undefined, pageSize, assetAllocatedDataPayload);
            const allRequestedAssets = await server.asset.allocation.search(undefined, pageSize, assetRequestedDataPayload);
            setState((prev) => ({
                ...prev,
                data: {
                    ...prev.data,
                    allocatedAssets: allAllocatedAssets,
                    requestedAssets: allRequestedAssets
                },
            }))
        } catch (e) {
            console.log("Error while fetching assets", e);
        } finally {
            setLoading(false);
        }
    }

    const handleRejectModalClose = () => {
        assetRejectForm.resetFields();
        setState((prev) => ({
            ...prev,
            modal: {
                ...prev.modal,
                assetReject: false,
            },
            selected: {
                ...prev.selected,
                RequestId: ""
            }
        }))
    }

    const handleAssetAllocateModalClose = () => {
        assetAllocateForm.resetFields();
        setState((prev) => ({
            ...prev,
            modal: {
                ...prev.modal,
                assetAllocate: false
            },
            selected: {
                ...prev.selected,
                AllocateRecord: null
            }
        }))
    }

    async function handleRejectionSubmit() {
        try {
            const formValues = await assetRejectForm.validateFields();
            await server.asset.request.reject(state.selected.RequestId, formValues.rejectMessage);
            message.success("Request rejected successfully!");
            await getAssets();
            handleRejectModalClose();
        } catch (e) {
            console.log("Error while rejecting request", e)
        }
    }

    async function handleAssetAllocateSubmit(formValues: IAllocateAssetsFormData) {
        const payload = allocateAssetModalMapper(formValues);
        if (!state.selected.AllocateRecord?.id) {
            return;
        }
        try {
            await server.asset.allocate(state.selected.AllocateRecord?.id, payload);
            message.success("Asset Allocated successfully!");
            await getAssets();
            handleAssetAllocateModalClose()
        } catch (e) {
            console.log("Error while submitting leave", e);
        }
    }

    async function handleAssetReturn(record: any) {
        try {
            await server.asset.return(record.asset.id, returnStatus);
            await getAssets();
        } catch (e) {
            console.log("Error while returning asset", e);
        }
    }

    async function handleAssetMissed(record: any) {
        try {
            await server.asset.missing(record.asset.id);
            await getAssets();
        } catch (e) {
            console.log("Error while submitting missing request", e)
        }
    }

    async function handleApproveRequest(record: any) {
        try {
            await server.asset.request.approve(record.id);
            message.success("Request approved successfully!");
            await getAssets();
        } catch (e) {
            console.log("Error while approving request", e)
        }
    }

    const allocatedAssetsColumns = [
        {
            title: "Category Name",
            dataIndex: "categoryName",
            key: "categoryName",
            render: (_: any, record: any) => {
                return record?.assetCategory?.name ? record?.assetCategory?.name : "-"
            }
        },
        {
            title: "Brand Name",
            dataIndex: "brandName",
            key: "brandName",
            render: (_: any, record: any) => {
                return record?.asset?.brandName ? record.asset.brandName : "-"
            }
        },
        {
            title: "Asset Code",
            dataIndex: "assetCode",
            key: "assetCode",
            render: (_: any, record: any) => {
                return record?.asset?.assetCode ? record.asset.assetCode : "-"
            }
        },
        {
            title: "Assigned Date",
            dataIndex: "assignedDate",
            key: "assignedDate",
            render: (_: any, record: any) => {
                return record?.assignedDate ? dayjs(record?.assignedDate).format(Locale.displayDate) : "-"
            }

        },
        {
            title: "Expected Return Date",
            dataIndex: "expectedReturnDate",
            key: "expectedReturnDate",
            render: (_: any, record: any) => {
                return record?.expectedReturnDate ? dayjs(record?.expectedReturnDate).format(Locale.displayDate) : "-"
            }
        },
        {
            title: "Returnable",
            dataIndex: "isReturnable",
            key: "isReturnable",
            render: (_: any, record: any) => (
                <p>
                    {record?.asset?.isReturnable ? "Yes" : "No"}
                </p>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (_: any, record: any) => {
                return <Tag className={`asset-allocation-status ${record.status.toLowerCase()}`}>
                    {record?.status ? removeUnderScore(record.status) : "-"}
                </Tag>
            },
        },
        {
            title: "Action",
            dataIndex: "action",
            key: "action",
            render: (_: any, record: any) => {
                return (
                    <div className={'pn-table-actions'}>
                        <Tooltip title={"Return Asset"}>
                            <Popconfirm
                                disabled={record.status === "RETURNED"}
                                title={"Select status"}
                                onConfirm={() => handleAssetReturn(record)}
                                description={
                                    <Select
                                        onChange={(value: any) => setReturnStatus(value)}
                                        placeholder={'Select Asset Status'}>
                                        {(Object.keys(Return_Asset_Status) as Array<keyof typeof Return_Asset_Status>).map((key) =>
                                            <Select.Option value={key} key={key}>
                                                {Return_Asset_Status[key]}
                                            </Select.Option>
                                        )}
                                    </Select>
                                }
                            >
                                <Undo2
                                    className={`action-edit action-return-asset ${record.status === "RETURNED" ? 'action-disabled' : ''}`}/>

                            </Popconfirm>
                        </Tooltip>

                        <Tooltip title={"Asset Missing"}>
                            <Popconfirm
                                okText={"Yes"}
                                title={`Are you sure ${record.asset.assetCode} is missed?`}
                                onConfirm={() => handleAssetMissed(record)}
                                classNames={{root: "hrms-app asset-confirm"}}
                            >
                                <TriangleAlert
                                    className={`action-mark-missing action-danger ${record.status === "MISSING" || record.status === "RETURNED" ? 'action-disabled' : ''} `}
                                />

                            </Popconfirm>
                        </Tooltip>

                    </div>
                )
            },
        },
    ]

    const requestedAssetsColumns = [
        {
            title: "Category Name",
            dataIndex: "categoryName",
            key: "categoryName",
            render: (_: any, record: any) => {
                return record?.assetCategory?.name ? record?.assetCategory?.name : "-"
            }
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (_: any, record: any) => {
                return <Tag className={`asset-allocation-status ${record.status.toLowerCase()}`}>
                    {record?.status ? removeUnderScore(record.status) : "-"}
                </Tag>
            },
        },
        {
            title: "Employee Remarks",
            dataIndex: "employeeRemarks",
            key: "employeeRemarks",
            render: (_: any, record: any) => {
                return record?.employeeRemarks ? record.employeeRemarks : "-"
            },
        },
        {
            title: "Actions",
            dataIndex: "actions",
            key: "actions",
            render: (_: any, record: any) => {
                return <>
                    <Tooltip title={"Approve Request"}>
                        <Popconfirm title={"Are you sure to approve request?"} okText={"Yes"}
                                    onConfirm={() => handleApproveRequest(record)}>
                            <Button icon={<CheckCircleOutlined/>} className={'asset-button asset-approve-button'}
                                    disabled={record.status != "REQUESTED"}/>
                        </Popconfirm>
                    </Tooltip>

                    <Tooltip title={"Allocate Asset"}>
                        <Button icon={<FolderAddOutlined/>} className={'asset-button asset-allocate-button'}
                                disabled={record.status != "REQUEST_ACCEPTED"}
                                onClick={() => {
                                    setState((prev) => ({
                                        ...prev,
                                        selected: {
                                            ...prev.selected,
                                            AllocateRecord: record
                                        },
                                        modal: {
                                            ...prev.modal,
                                            assetAllocate: true
                                        },
                                    }))
                                }}
                        />
                    </Tooltip>

                    <Tooltip title={"Reject Request"}>
                        <Button icon={<CloseCircleOutlined/>} className={'asset-button asset-reject-button'}
                                disabled={record?.status != "REQUESTED"}
                                onClick={() => {
                                    setState((prev) => ({
                                        ...prev,
                                        modal: {
                                            ...prev.modal,
                                            assetReject: true
                                        },
                                        selected: {
                                            ...prev.selected,
                                            RequestId: record.id
                                        }
                                    }))
                                }}/>
                    </Tooltip>
                </>
            }
        }
    ]

    return (
        <>
            <div className={'assets-tab-header'}>
                <h1 className={'allocated-assets-tab-heading'}>Assets</h1>
            </div>

            {
                loading ? (
                    <LoadingSkeleton loading={true}>
                        <Grid
                            bordered={true}
                            dataSource={[]}
                            columns={allocatedAssetsColumns}
                        />
                    </LoadingSkeleton>
                ) : state.data.allocatedAssets.length > 0 ? (
                    <div className={'allocated-assets-tab-table'}>
                        <Grid
                            bordered={true}
                            dataSource={state.data.allocatedAssets}
                            columns={allocatedAssetsColumns}
                        />
                    </div>
                ) : (
                    <EmptyData
                        icon={<InboxOutlined className={'asset-empty-data-icon'}/>}
                        title={"No Assets Assigned"}
                        subtitle={
                            user?.isAdmin
                                ? "Once assets are assigned, they will appear here"
                                : "Once assets are assigned to you, they will appear here or you can request an asset to get started!"
                        }
                    />
                )
            }
            {
                state.data.requestedAssets.length > 0 &&
                <>
                    <h1 className={'requested-assets-tab-heading'}>Requested Assets</h1>
                    <div className={'requested-assets-tab-table'}>
                        <Grid
                            bordered={true}
                            dataSource={state.data.requestedAssets}
                            columns={requestedAssetsColumns}
                        />
                    </div>
                </>
            }

            <AssetRejectModal
                assetRejectModal={state.modal.assetReject}
                handleRejectModalClose={handleRejectModalClose}
                assetRejectForm={assetRejectForm}
                handleRejectionSubmit={handleRejectionSubmit}
            />

            <AssetAllocateModal
                assetAllocateForm={assetAllocateForm}
                selectedAssetAllocatedRecord={state.selected.AllocateRecord}
                assetAllocateModal={state.modal.assetAllocate}
                handleAssetAllocateModalClose={handleAssetAllocateModalClose}
                handleAssetAllocateSubmit={handleAssetAllocateSubmit}
            />
        </>
    )
}

export default AssetsTab;
